// ResponseSetRowActions — the response-sets table's Actions cell: a kebab
// Dropdown of the three per-set actions. All three are the same flows the
// selection bar runs, handed down from FormResponsesModal as callbacks, so a
// row's menu and a one-row selection do exactly the same thing. Owns its own
// open state (one row's menu at a time).
//
// Modelled on FormRowActions: `portal` because the table scrolls, so an
// in-flow panel would be cropped by the overflow container.

import { useState, type MouseEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Icon, IconButton } from '@/components/common';

interface ResponseSetRowActionsProps {
  onEdit: () => void;
  /** Creates a copy of this set — there is no duplicate endpoint. */
  onDuplicate: () => void;
  onDelete: () => void;
}

const ResponseSetRowActions = ({ onEdit, onDuplicate, onDelete }: ResponseSetRowActionsProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Keep the menu's clicks out of the row beneath it.
  const stop = (e: MouseEvent) => e.stopPropagation();

  const item = (
    label: string,
    icon: string,
    onClick: () => void,
    opts: { danger?: boolean } = {},
  ): ReactNode => (
    <button
      type='button'
      className={`app-dropdown__item${opts.danger ? ' app-dropdown__item--danger' : ''}`}
      role='menuitem'
      onClick={() => {
        setOpen(false);
        onClick();
      }}
    >
      <Icon name={icon} size={15} />
      <span className='app-dropdown__item-label'>{label}</span>
    </button>
  );

  return (
    <span className='forms-table__actions' onClick={stop}>
      <Dropdown
        open={open}
        onClose={() => setOpen(false)}
        align='end'
        width={200}
        portal
        trigger={
          <IconButton
            icon='ellipsis-vertical'
            variant='ghost'
            size='sm'
            aria-label={t('forms_col_actions')}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          />
        }
      >
        {item(t('forms_action_edit'), 'pencil', onEdit)}
        {item(t('forms_action_duplicate'), 'copy', onDuplicate)}

        <div className='app-dropdown__divider' role='separator' />

        {item(t('forms_action_delete'), 'trash-2', onDelete, { danger: true })}
      </Dropdown>
    </span>
  );
};

export default ResponseSetRowActions;
